import { useEffect, useMemo } from 'react';
import { Flex, Separator, Spinner, toaster } from '@optiaxiom/react';
import { useQueryClient } from '@tanstack/react-query';

import { EmptyPage } from '@frontend/components';
import { OPAL_URL } from '@frontend/envConfig';
import { useOpalUser, useOperations } from '@frontend/hooks';
import { ProviderConnectionSummary, Tool } from '@frontend/types';

import {
  useGetAuthProviders,
  useGetTools,
  useGetUserAuthConnections,
  useInitiateAuthConnection,
  useRevokeAuthConnection,
} from '../../hooks';

import { ProviderConnectionGrid } from './ProviderConnectionGrid';
import { RemoteMCPPlaceholder } from './RemoteMCPPlaceholder';

const OPTIID_IDENTIFIERS = ['optiid', 'opti_id', 'opti-id'];

const isOptiIdProvider = (name: string, authType: string): boolean => {
  const lowerName = name.toLowerCase();
  const lowerAuthType = authType.toLowerCase();
  return OPTIID_IDENTIFIERS.some(id => lowerName.includes(id)) || lowerAuthType === 'optiid_sso';
};

export function AuthConnectionsTab() {
  const { opalUser } = useOpalUser();
  const { canUser } = useOperations();
  const queryClient = useQueryClient();
  const canManageRemoteMCP = canUser('manage_remote_mcp');

  const instanceId = opalUser?.instance?.instance_id;
  const userId = opalUser?.id;

  const {
    connections,
    isPending: isLoadingConnections,
    error: connectionsError,
  } = useGetUserAuthConnections(userId || '');
  const {
    toolsResponse,
    isPending: isLoadingTools,
    error: toolsError,
  } = useGetTools(instanceId || '');
  const { providers, isPending: isLoadingProviders, error: providersError } = useGetAuthProviders();

  const { initiateAuth, isInitiating } = useInitiateAuthConnection((authUrl: string) => {
    // Open as popup to preserve window.opener for postMessage communication
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    window.open(
      authUrl,
      'oauth_popup',
      `popup,width=${width},height=${height},left=${left},top=${top}`
    );
  });
  const { mutate: revokeConnection } = useRevokeAuthConnection(userId || '');

  useEffect(() => {
    const error = connectionsError || toolsError || providersError;
    if (error) {
      toaster.create(error.message || 'Failed to load auth connections', { type: 'danger' });
    }
  }, [connectionsError, toolsError, providersError]);

  // Listen for OAuth completion messages from popup window
  useEffect(() => {
    const handleOAuthComplete = (data: { success: boolean; error?: string }) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['user-auth-connections', userId] });
        toaster.create('Successfully connected!', { type: 'success' });
      } else {
        toaster.create(data.error || 'Connection failed', { type: 'danger' });
      }
    };

    // Handler for postMessage (if window.opener works)
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'oauth_complete') {
        handleOAuthComplete(event.data);
      }
    };

    // Handler for localStorage storage event (fallback for cross-origin popups)
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'oauth_callback_result' && event.newValue) {
        try {
          const data = JSON.parse(event.newValue);
          if (data.type === 'oauth_complete') {
            handleOAuthComplete(data);
            // Clean up the localStorage key
            localStorage.removeItem('oauth_callback_result');
          }
        } catch {
          // Ignore parse errors
        }
      }
    };

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, [queryClient, userId]);

  const { neededProviders, unusedProviders } = useMemo((): {
    neededProviders: ProviderConnectionSummary[];
    unusedProviders: ProviderConnectionSummary[];
  } => {
    if (!providers || !toolsResponse?.functions) {
      return { neededProviders: [], unusedProviders: [] };
    }

    // Filter out OptiID providers
    const externalProviders = providers.filter(p => !isOptiIdProvider(p.name, p.auth_type));

    // Build a map of provider -> tools
    const providerToolsMap = new Map<string, string[]>();

    toolsResponse.functions.forEach((tool: Tool) => {
      if (tool.auth_requirements && tool.auth_requirements.length > 0) {
        tool.auth_requirements.forEach(req => {
          const providerName = req.provider;
          if (providerName && !isOptiIdProvider(providerName, '')) {
            const existing = providerToolsMap.get(providerName) || [];
            if (!existing.includes(tool.name)) {
              existing.push(tool.name);
            }
            providerToolsMap.set(providerName, existing);
          }
        });
      }
    });

    // Build summaries for provider + bundle combinations
    // Only include approved scope bundles
    const allSummaries: ProviderConnectionSummary[] = [];

    externalProviders.forEach(provider => {
      const approvedBundles = (provider.scope_bundles || []).filter(b => b.status === 'approved');

      // Skip providers with no approved bundles
      if (approvedBundles.length === 0) {
        return;
      }

      approvedBundles.forEach(bundle => {
        // Find connection matching both provider and bundle
        const userConnection = connections.find(
          c => c.provider === provider.name && c.scope_bundle?.id === bundle.id
        );

        let connectionStatus: ProviderConnectionSummary['connectionStatus'] = 'not_connected';
        if (userConnection) {
          if (userConnection.status === 'active') {
            if (userConnection.expires_at) {
              const expiresAt = new Date(userConnection.expires_at);
              connectionStatus = expiresAt < new Date() ? 'expired' : 'connected';
            } else {
              connectionStatus = 'connected';
            }
          } else if (userConnection.status === 'expired') {
            connectionStatus = 'expired';
          }
        }

        allSummaries.push({
          providerId: provider.id,
          providerName: provider.name,
          providerDescription: provider.description,
          authType: provider.auth_type,
          scopeBundleId: bundle.id,
          scopeBundleName: bundle.name,
          scopeBundleDescription: bundle.description,
          connectionStatus,
          toolsUsingProvider: providerToolsMap.get(provider.name) || [],
          connection: userConnection,
        });
      });
    });

    // Split into needed (has tools) and unused (no tools)
    return {
      neededProviders: allSummaries.filter(p => p.toolsUsingProvider.length > 0),
      unusedProviders: allSummaries.filter(p => p.toolsUsingProvider.length === 0),
    };
  }, [providers, toolsResponse, connections]);

  const handleConnect = (providerName: string, scopeBundleId: string) => {
    if (!userId) return;
    const redirectUri = `${OPAL_URL}/apps/api/oauth/callback/${encodeURIComponent(providerName)}`;
    initiateAuth({
      provider: providerName,
      scopeBundleId,
      userId,
      redirectUri,
    });
  };

  const handleDisconnect = (credentialId: string) => {
    revokeConnection(credentialId);
  };

  const isLoading = isLoadingConnections || isLoadingTools || isLoadingProviders;

  if (isLoading) {
    return (
      <Flex justifyContent="center" p="24">
        <Spinner />
      </Flex>
    );
  }

  const hasAnyProviders = neededProviders.length > 0 || unusedProviders.length > 0;

  return (
    <Flex flexDirection="column" gap="24">
      {!hasAnyProviders ? (
        <EmptyPage
          title="No authentication providers configured"
          description="No OAuth providers are available. Contact your administrator to set up authentication providers."
        />
      ) : (
        <Flex flexDirection="column" gap="24">
          {/* Providers Needed - has tools requiring them */}
          {neededProviders.length > 0 && (
            <Flex flexDirection="column" gap="12">
              <ProviderConnectionGrid
                providers={neededProviders}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                isConnecting={isInitiating}
              />
            </Flex>
          )}

          {/* Providers Unused - no tools require them yet */}
          {unusedProviders.length > 0 && (
            <Flex flexDirection="column" gap="12">
              <ProviderConnectionGrid
                providers={unusedProviders}
                onConnect={handleConnect}
                onDisconnect={handleDisconnect}
                isConnecting={isInitiating}
              />
            </Flex>
          )}
        </Flex>
      )}

      {canManageRemoteMCP && (
        <>
          <Separator />
          <RemoteMCPPlaceholder />
        </>
      )}
    </Flex>
  );
}
